import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ParticipantCampaignDto, ParticipantCampaignSurveyListForm, ParticipantCampaignSurveyModalForm, ParticipantSurveyDto } from "../../types";
import AppTable, { ColumnConfig } from "../../../../../components/AppTable/AppTable";
import ModalGlobal from "../../../../../components/AppModal/ModalGlobal";
import { useEffect, useState } from "react";
import { getSurveysByCampaignId } from "../../../surveys/api/surveyAPI";
import { useAlert } from "../../../../../hooks/useAlert";
import AlertMessage from "../../../../../components/AlertMessage";
import { ApiErrorResponse } from "../../../../../types/ApiErrorResponse";
import { SectionPermissionTypeEnum } from "../../../../../types/SectionPermissionTypesEnum";

interface ParticipantCampaignsFormProps {
    selectedCampaigns: ParticipantCampaignSurveyListForm[];
    campaignOptions: ParticipantCampaignDto[];
    onAddCampaign: (surveyCampaign: ParticipantCampaignSurveyListForm) => void;
    onDeleteCampaign: (surveyCampaign: ParticipantCampaignSurveyListForm) => void; 
}

export default function ParticipantCampaignsForm({ selectedCampaigns, campaignOptions, onAddCampaign, onDeleteCampaign }: ParticipantCampaignsFormProps) {
    const { t } = useTranslation(['participant', 'common']);
    const { showAlert } = useAlert({ fromLocation: true });

    const [ showAddCampaignModal, setShowAddCampaignModal ] = useState<boolean>(false)
    const [ selectedParticipantCampaign, setSelectedCampign ] = useState<ParticipantCampaignSurveyModalForm>({});
    const [ surveyOptions, setSurveyOptions ] = useState<ParticipantSurveyDto[]>([])
    const [ errors, setErrors ] = useState<{ [key: keyof ParticipantCampaignSurveyModalForm | string]: string; }>({});

    const columns: ColumnConfig<ParticipantCampaignSurveyListForm>[] = [
        { header: t("participant:campaignList.campaignName"), key: "campaignName" },
        { header: t("participant:campaignList.campaignStatus"), key: "campaignStatus", render: (item) => <>{item.campaignStatus ? t(`participant:campaignStatus.${item.campaignStatus}`) : "-"}</> },
        { header: t("participant:campaignList.surveyName"), key: "surveyName" },
        { header: t("participant:campaignList.surveyStatus"), key: "surveyStatus", render: (item) => <>{item.surveyStatus ? t(`participant:surveyStatus.${item.surveyStatus}`) : "-"}</> }
    ];

    const addNewCampaign = () => {
        setErrors({});
        const selectedCampaign = campaignOptions.find(item => item.id == selectedParticipantCampaign.campaignId);
        const selectedSurvey = surveyOptions.find(item => item.id == selectedParticipantCampaign.surveyId);
        let isValid = true;

        if (selectedCampaign == null) {
            isValid = false;
            setErrors({...errors, ["campaignId"]: t("common:form.requiredFieldForm")})
        }

        if (
            selectedCampaigns.some(participantCampaign =>
                participantCampaign.dataBaixa == null
                && (selectedSurvey ? selectedParticipantCampaign.surveyId === participantCampaign.surveyId : selectedParticipantCampaign.surveyId)
                && selectedParticipantCampaign.campaignId === participantCampaign.campaignId)
        ) {
            isValid = false;
            setErrors({...errors, ["alreadyExist"]: t("participant:form.alreadyExist")})
        }

        if (isValid) {
            onAddCampaign({
                id: selectedParticipantCampaign.id ?? 0,
                campaignId: selectedCampaign!.id,
                campaignName: selectedCampaign!.name,
                campaignStatus: selectedCampaign!.status,
                surveyId: selectedSurvey?.id,
                surveyName: selectedSurvey?.name,
                surveyStatus: selectedSurvey?.status
            });
            setShowAddCampaignModal(false);
        }
    }

    const fetchSurveysByCampaign = async (campaignId?: number) => {
        try {
            if (campaignId) {
                const surveysByCampaign = await getSurveysByCampaignId(campaignId);

                setSurveyOptions(surveysByCampaign.filter(survey => 
                    !selectedCampaigns.some(participantCampaign => participantCampaign.dataBaixa == null && survey.id === participantCampaign.surveyId)
                ));
                return surveysByCampaign;
            }
            else {
                setSurveyOptions([]);
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError.message,
                type: 'danger',
                duration: 8000
            });
        }
    };

    useEffect(() => {
        fetchSurveysByCampaign(selectedParticipantCampaign.campaignId);
    }, [selectedParticipantCampaign.campaignId]);

    return (
        <>
            <ModalGlobal
                show={showAddCampaignModal}
                onClose={() => setShowAddCampaignModal(false)}
                title={t("participant:addCampaignTitle")}
                onSave={addNewCampaign}
                body={
                    <Form>
                        {!!errors["alreadyExist"] && <Row>
                            <Col xs={12} md={12}>
                                <AlertMessage
                                    message={errors["alreadyExist"]}
                                    type={"danger"}
                                    // displayTime={5000}
                                />
                            </Col>                        
                        </Row>}
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("participant:campaign")}</Form.Label>
                                    <Form.Select
                                        value={selectedParticipantCampaign?.campaignId}
                                        isInvalid={!!errors["campaignId"]}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            setSelectedCampign({...selectedParticipantCampaign, ["campaignId"]: parseInt(e.target.value)});
                                        }}
                                    >
                                        <option></option>
                                        {campaignOptions.map(campaign => (<option value={campaign.id}>{campaign.name}</option>))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors["campaignId"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("participant:survey")}</Form.Label>
                                    <Form.Select
                                        value={selectedParticipantCampaign?.surveyId}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            selectedParticipantCampaign.surveyId = parseInt(e.target.value)
                                        }}
                                    >
                                        <option></option>
                                        {surveyOptions.map(survey => (<option value={survey.id}>{survey.name}</option>))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                }
                saveText={t("common:buttons.buttonAccept")}
                closeText={t("common:buttons.buttonCancel")}
                isLoading={false}
            />
            <Row style={{ marginTop: '40px' }}>
                <Col xs={8} md={8}>
                    <h5>{t("participant:new.headerCampaingParticipant")}</h5>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <Button onClick={() => {
                        setShowAddCampaignModal(true);
                        setSelectedCampign({});
                    }}>{t("participant:addCampaign")}</Button>
                </Col>
            </Row>
            <AppTable<ParticipantCampaignSurveyListForm>
                data={selectedCampaigns.filter(x => x.dataBaixa == null) || []}
                columns={columns}
                emptyMessage={t("participant:nonParticipants")}
                t={t}
                hasActions={true}
                onDelete={onDeleteCampaign}
                hasPagination={false}
                sectionPermission={SectionPermissionTypeEnum.PARTICIPANTS}
            />
        </>
    );
}

